import type {
  ActiveViewsStatus,
  ViewHoldKind,
  ViewHoldPayload,
  ViewHoldReleasePayload,
  ViewHolds,
} from './types';

const NO_HOLDS: ViewHolds = {};

const LOADING_KINDS: readonly ViewHoldKind[] = ['data', 'layout'];

const getHolds = (
  status: ActiveViewsStatus,
  viewInstanceId: string
): ViewHolds => status[viewInstanceId] ?? NO_HOLDS;

const findKindHeldBy = (
  holds: ViewHolds,
  token: string
): ViewHoldKind | undefined =>
  (Object.keys(holds) as ViewHoldKind[]).find(
    kind => holds[kind]?.token === token
  );

export const isViewRegistered = (
  status: ActiveViewsStatus,
  viewInstanceId: string
): boolean => Object.hasOwn(status, viewInstanceId);

export const registerView = (
  status: ActiveViewsStatus,
  viewInstanceId: string
): ActiveViewsStatus => {
  if (isViewRegistered(status, viewInstanceId)) {
    return status;
  }
  return { ...status, [viewInstanceId]: NO_HOLDS };
};

export const unregisterView = (
  status: ActiveViewsStatus,
  viewInstanceId: string
): ActiveViewsStatus => {
  if (!isViewRegistered(status, viewInstanceId)) {
    return status;
  }
  const { [viewInstanceId]: _removed, ...rest } = status;
  return rest;
};

export const takeHold = (
  status: ActiveViewsStatus,
  { viewInstanceId, kind, token, since }: ViewHoldPayload
): ActiveViewsStatus => {
  if (!isViewRegistered(status, viewInstanceId)) {
    return status;
  }
  return {
    ...status,
    [viewInstanceId]: {
      ...getHolds(status, viewInstanceId),
      [kind]: { token, since },
    },
  };
};

export const releaseHold = (
  status: ActiveViewsStatus,
  { viewInstanceId, token }: ViewHoldReleasePayload
): ActiveViewsStatus => {
  const holds = getHolds(status, viewInstanceId);
  const kind = findKindHeldBy(holds, token);
  if (!kind) {
    return status;
  }
  const { [kind]: _released, ...rest } = holds;
  return { ...status, [viewInstanceId]: rest };
};

export const getViewHolds = getHolds;

export const isViewLoading = (
  status: ActiveViewsStatus,
  viewInstanceId: string
): boolean => {
  const holds = getHolds(status, viewInstanceId);
  return LOADING_KINDS.some(kind => holds[kind]);
};

export const isViewSettled = (
  status: ActiveViewsStatus,
  viewInstanceId: string
): boolean =>
  isViewRegistered(status, viewInstanceId) &&
  Object.keys(getHolds(status, viewInstanceId)).length === 0;
